import type { CrmContact, DispatchIdentity, DispatchIdentityRole } from '../../api/types';

/**
 * Who a phone number belongs to, according to dispatch.
 *
 * A thread that arrived over SMS, WhatsApp or a call carries no name — the
 * service stores the number as the contact's name, so the list would otherwise
 * read "+15165550100" about someone dispatch has billed for years. The service
 * resolves the number against dispatch and hands back every actor that shares
 * it (services/dispatchIdentity.js); this names the thread from that answer the
 * way the dispatch dashboard's SMS inbox does, so the same number reads the
 * same in both consoles.
 *
 * The match is on the last ten digits, which is looser than the exact rule
 * behind `dispatchLinkVerified`. That is why the role chip always shows beside
 * the name: an agent should see WHAT dispatch matched, not silently inherit it.
 *
 * Pure on purpose — the chip that renders these lives in DispatchRoleChip.tsx.
 */

/** Drivers are "Chauffeurs" everywhere this product faces an operator. */
const ROLE_LABELS: Record<DispatchIdentityRole, string> = {
  affiliate: 'Affiliate',
  customer: 'Customer',
  passenger: 'Passenger',
  driver: 'Chauffeur',
};

export function roleLabel(role: DispatchIdentityRole): string {
  return ROLE_LABELS[role] ?? role;
}

/** The one actor to show where there is only room for one. */
export function primaryIdentity(
  contact: Pick<CrmContact, 'dispatchIdentity'>,
): DispatchIdentity | null {
  return contact.dispatchIdentity?.[0] ?? null;
}

/**
 * A stored name that is really just the contact's own number — what every phone
 * channel writes when the provider supplies no profile name.
 */
export function nameIsJustDigits(name: string | null | undefined): boolean {
  const trimmed = (name ?? '').trim();
  return trimmed !== '' && !/[a-z]/i.test(trimmed);
}

/**
 * The name to put on a thread. Dispatch wins only where we have nothing better:
 * an email, a Telegram/WhatsApp profile name or a name an agent typed is closer
 * to how this person wants to be addressed than dispatch's billing record.
 *
 * The service applies the same rule when it renames the contact row, so this is
 * the display half of one decision rather than a second, conflicting one. It
 * still has to exist: the row is renamed only while its name is the bare
 * number, and a contact named by an agent keeps that name here too.
 */
export function contactDisplayName(contact: CrmContact): string {
  const stored = (contact.name ?? '').trim();
  if (stored && !nameIsJustDigits(stored)) return stored;
  return primaryIdentity(contact)?.name || stored || contact.email || contact.phoneNumber || 'Unknown';
}
